import dayjs from 'dayjs';
import * as z from 'zod';

import { emailHistorySourceTypeSchema } from '@/_pages/settings/email-history/shared/schemas/email-history-query-schema';
import { emailTemplateQueryLinkerSchema } from '@/_pages/settings/email-templates/shared/schemas/email-templates-query-linker-schema';
import { dateTime } from '@/_shared';

import { invoiceEmailDeliveryStatusSchema } from './invoice-email-delivery-status-schema';

export const sendInvoiceEmailBodySchema = z.object({
  email_template_id: z.number(),
  contact_ids: z.array(z.number()).min(1),
  /** UTC ISO 8601 with `Z` suffix (e.g. `2026-06-11T07:00:00.000Z`). */
  scheduled_at: z.string().nullable().optional(),
});

export type SendInvoiceEmailBodySchema = z.infer<typeof sendInvoiceEmailBodySchema>;

export const invoiceEmailActionSchema = z.enum(['send', 'schedule']);
export type InvoiceEmailActionSchema = z.infer<typeof invoiceEmailActionSchema>;

export const invoiceEmailSendFormSchema = z
  .object({
    email_action: invoiceEmailActionSchema,
    template: emailTemplateQueryLinkerSchema.nullable().refine((template) => template?.id != null, {
      message: 'Please select an email template.',
    }),
    scheduled_at: z.string().nullable(),
  })
  .superRefine((data, ctx) => {
    if (data.email_action !== 'schedule') {
      return;
    }

    if (!data.scheduled_at?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please select a date and time.',
        path: ['scheduled_at'],
      });
      return;
    }

    const scheduled = dateTime.parse(data.scheduled_at);
    if (!scheduled.isValid()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please enter a valid date and time.',
        path: ['scheduled_at'],
      });
      return;
    }

    if (!scheduled.isAfter(dateTime.now())) {
      ctx.addIssue({
        code: 'custom',
        message: 'Scheduled time cannot be in the past.',
        path: ['scheduled_at'],
      });
    }
  });

export type InvoiceEmailSendFormSchema = z.infer<typeof invoiceEmailSendFormSchema>;

export function getDefaultScheduledAt(): string {
  const timeZone = dateTime.getDeviceTimeZone();
  const now = dayjs().tz(timeZone);
  let candidate = now.add(1, 'hour').startOf('hour');

  if (candidate.isSameOrBefore(now)) {
    candidate = candidate.add(1, 'hour');
  }

  return candidate.format();
}

export function getScheduleMinDateTime(): Date {
  return dateTime.now().toDate();
}

export function formatScheduledAtSummary(scheduledAt: string | null | undefined): string | null {
  if (!scheduledAt) {
    return null;
  }

  const formatted = dateTime.formatDateTimeWithTimeZone(scheduledAt);
  return formatted || null;
}

export const sendInvoiceEmailMetadataSchema = z.object({
  variables: z
    .object({
      company: z.string().optional(),
      company_account_number: z.string().optional(),
      due_date: z.string().optional(),
      brand: z.string().optional(),
      brand_email_address: z.string().optional(),
      billing_email_address: z.string().optional(),
      legal_name: z.string().optional(),
      payable_to: z.string().optional(),
      online_payment_url: z.string().optional(),
      brand_primary_phone: z.string().optional(),
      invoice_amount: z.string().optional(),
      agreement_number: z.string().optional(),
      invoice_number: z.string().optional(),
    })
    .optional(),
  from: z.string().optional(),
  to: z.array(z.string()).optional(),
  invoice_type: z.string().optional(),
});

export type SendInvoiceEmailMetadataSchema = z.infer<typeof sendInvoiceEmailMetadataSchema>;

export const sendInvoiceEmailDeliveryDetailsSchema = z.object({
  provider: z.string().optional(),
  message_id: z.string().optional(),
  attachment: z.number().optional(),
});

export type SendInvoiceEmailDeliveryDetailsSchema = z.infer<typeof sendInvoiceEmailDeliveryDetailsSchema>;

export const sendInvoiceEmailHistoryDataSchema = z.object({
  id: z.number(),
  source_type: emailHistorySourceTypeSchema,
  source_id: z.number(),
  email_template_id: z.number(),
  brand_id: z.number(),
  client_id: z.number(),
  sender_id: z.number(),
  email_subject: z.string(),
  email_body: z.string(),
  email_metadata: sendInvoiceEmailMetadataSchema,
  delivery_details: sendInvoiceEmailDeliveryDetailsSchema,
  delivery_status: invoiceEmailDeliveryStatusSchema,
  scheduled_at: z.string().nullable().optional(),
  sent_at: z.string().nullable(),
});

export type SendInvoiceEmailHistoryDataSchema = z.infer<typeof sendInvoiceEmailHistoryDataSchema>;

export const sendInvoiceEmailResponseSchema = z.object({
  data: sendInvoiceEmailHistoryDataSchema,
});

export type SendInvoiceEmailResponseSchema = z.infer<typeof sendInvoiceEmailResponseSchema>;

export function parseSendInvoiceEmailResponse(response: unknown): SendInvoiceEmailResponseSchema | null {
  const parsed = sendInvoiceEmailResponseSchema.safeParse(response);
  return parsed.success ? parsed.data : null;
}

export function getInvoiceNumberFromSendEmailResponse(response: unknown): string | null {
  const parsed = parseSendInvoiceEmailResponse(response);
  const invoiceNumber = parsed?.data.email_metadata.variables?.invoice_number?.trim();
  return invoiceNumber || null;
}

function isScheduledSendResponse(parsed: SendInvoiceEmailResponseSchema | null): boolean {
  if (!parsed) {
    return false;
  }

  return parsed.data.delivery_status === 'scheduled' || Boolean(parsed.data.scheduled_at && !parsed.data.sent_at);
}

export function getInvoiceEmailSendSuccessMessage(response: unknown, fallbackInvoiceNumber?: string | null): string {
  const parsed = parseSendInvoiceEmailResponse(response);
  const invoiceNumber = getInvoiceNumberFromSendEmailResponse(response) || fallbackInvoiceNumber?.trim() || null;

  if (isScheduledSendResponse(parsed) && parsed?.data.scheduled_at) {
    const formatted = formatScheduledAtSummary(parsed.data.scheduled_at);

    if (formatted && invoiceNumber) {
      return `Invoice email scheduled for ${invoiceNumber} on ${formatted}.`;
    }

    if (formatted) {
      return `Invoice email scheduled for ${formatted}.`;
    }
  }

  if (invoiceNumber) {
    return `Invoice email sent for ${invoiceNumber}.`;
  }

  return 'Invoice email sent.';
}

export type InvoiceEmailActionOption = {
  id: InvoiceEmailActionSchema;
  name: string;
};

export const INVOICE_EMAIL_ACTION_OPTIONS: InvoiceEmailActionOption[] = [
  { id: 'send', name: 'Send now' },
  { id: 'schedule', name: 'Schedule for later' },
];

export function getInvoiceEmailActionOptions(scheduleEnabled: boolean): InvoiceEmailActionOption[] {
  if (scheduleEnabled) {
    return INVOICE_EMAIL_ACTION_OPTIONS;
  }

  return INVOICE_EMAIL_ACTION_OPTIONS.filter((option) => option.id === 'send');
}

export function getInvoiceEmailActionOption(action: InvoiceEmailActionSchema): InvoiceEmailActionOption {
  return INVOICE_EMAIL_ACTION_OPTIONS.find((option) => option.id === action) ?? INVOICE_EMAIL_ACTION_OPTIONS[0];
}
